// frontend/src/components/planner/HistoricalRegister.jsx
// Phase 2.6A: Historical Register - Checkbook-style view of past transactions

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { format, subDays, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import {
  RefreshCw,
  Search,
  Calendar,
  Building2,
  Tag,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  Link2,
  Unlink,
  AlertCircle,
  TrendingUp,
  CreditCard,
  Banknote,
  FileText,
  Clock,
  Plus,
  Camera
} from 'lucide-react';
import api from '../../utils/api';
import { useBusinessEntity } from '../../contexts/BusinessEntityContext';
import AppShell from '../layout/AppShell';
import ContextMenu from './ContextMenu';
import CreateRecurringModal from './CreateRecurringModal';
import LinkToForecastModal from './LinkToForecastModal';
import ReceiptViewer from '../common/ReceiptViewer';

// Status configuration
const STATUS_CONFIG = {
  cleared: { label: 'Cleared', color: 'text-green-600', bg: 'bg-green-50', icon: Check },
  paid: { label: 'Paid', color: 'text-blue-600', bg: 'bg-blue-50', icon: Check },
  pending: { label: 'Upcoming', color: 'text-amber-600', bg: 'bg-amber-50', icon: AlertCircle },
  pending_at_bank: { label: 'Pending', color: 'text-purple-600', bg: 'bg-purple-50', icon: Clock },
  skipped: { label: 'Skipped', color: 'text-gray-400', bg: 'bg-gray-50', icon: X }
};

// Type configuration
const TYPE_CONFIG = {
  income: { icon: TrendingUp, color: 'text-emerald-600', amountColor: 'text-emerald-600' },
  bill: { icon: FileText, color: 'text-red-600', amountColor: 'text-red-600' },
  debt: { icon: CreditCard, color: 'text-orange-600', amountColor: 'text-orange-600' }
};

// Date range presets
const DATE_PRESETS = [
  { id: 'last30', label: 'Last 30 Days', getDates: () => ({ start: subDays(new Date(), 30), end: new Date() }) },
  { id: 'last90', label: 'Last 90 Days', getDates: () => ({ start: subDays(new Date(), 90), end: new Date() }) },
  { id: 'thisMonth', label: 'This Month', getDates: () => ({ start: startOfMonth(new Date()), end: endOfMonth(new Date()) }) },
  { id: 'lastMonth', label: 'Last Month', getDates: () => {
    const lastMonth = subDays(startOfMonth(new Date()), 1);
    return { start: startOfMonth(lastMonth), end: endOfMonth(lastMonth) };
  }},
  { id: 'custom', label: 'Custom Range', getDates: () => null }
];

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = parseISO(dateStr + 'T00:00:00');
  return format(date, 'MMM d, yyyy');
}

function formatShortDate(dateStr) {
  if (!dateStr) return '';
  const date = parseISO(dateStr + 'T00:00:00');
  return format(date, 'MMM d');
}

export default function HistoricalRegister() {
  const { activeFilter, entities } = useBusinessEntity();

  // Data state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [datePreset, setDatePreset] = useState('last90');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [_showFilters, setShowFilters] = useState(false); // eslint-disable-line no-unused-vars
  
  // Reference data
  const [accountsList, setAccountsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  
  // Grouping state
  const [expandedMonths, setExpandedMonths] = useState({});
  
  // Data freshness
  const [plaidDataAsOf, setPlaidDataAsOf] = useState(null);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null); // { x, y, txn }
  const [receiptViewerTxId, setReceiptViewerTxId] = useState(null);
  
  // Create recurring modal state
  const [createRecurringTxn, setCreateRecurringTxn] = useState(null); // txn to prepopulate from
  
  // Link to forecast modal state
  const [linkToForecastTxn, setLinkToForecastTxn] = useState(null); // txn to link

  // Load accounts on mount
  useEffect(() => {
    loadAccounts();
  }, []);

  // Load transactions when filters change
  useEffect(() => {
    loadTransactions();
  }, [selectedAccountId, datePreset, customStartDate, customEndDate]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAccounts() {
    try {
      const response = await api.get('/api/planner/accounts');
      if (response.data.success) {
        setAccountsList(response.data.accounts || []);
      }
    } catch (err) {
      console.error('Error loading accounts:', err);
    }
  }

  async function loadTransactions() {
    try {
      setLoading(true);
      setError('');
      
      // Calculate date range
      let startDate, endDate;
      if (datePreset === 'custom') {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const preset = DATE_PRESETS.find(p => p.id === datePreset);
        if (preset) {
          const dates = preset.getDates();
          startDate = format(dates.start, 'yyyy-MM-dd');
          endDate = format(dates.end, 'yyyy-MM-dd');
        }
      }
      
      const params = new URLSearchParams();
      params.append('filter', 'cleared');
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedAccountId) params.append('bankAccountId', selectedAccountId);
      
      const response = await api.get(`/api/planner/register?${params.toString()}`);
      
      if (response.data.success) {
        const txns = response.data.transactions || response.data.register || [];
        setTransactions(txns);
        
        // Capture Plaid data freshness
        if (response.data.summary?.latestPlaidUpdate) {
          setPlaidDataAsOf(response.data.summary.latestPlaidUpdate);
        }
        
        // Extract unique categories
        const cats = [...new Set(txns.map(t => t.category).filter(Boolean))];
        setCategoriesList(cats.sort());
        
        // Initialize all months as expanded
        const months = groupByMonth(txns);
        const initialExpanded = {};
        Object.keys(months).forEach(key => {
          initialExpanded[key] = true;
        });
        setExpandedMonths(initialExpanded);
      }
    } catch (err) {
      console.error('Error loading transactions:', err);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadTransactions();
    setRefreshing(false);
  }

  async function handleUnmatch(txn) {
    if (!window.confirm(`Unmatch "${txn.merchant_name || txn.name}" from "${txn.matched_forecast?.name}"?\n\nThe forecast item will return to your upcoming list.`)) {
      return;
    }
    try {
      const response = await api.post('/api/planner/unmatch', {
        plaidTransactionId: txn.plaid_transaction_id,
        scheduledTransactionId: txn.matched_transaction_id
      });
      if (response.data.success) {
        // Reload to reflect the change
        await loadTransactions();
      }
    } catch (err) {
      console.error('Error unmatching:', err);
      setError('Failed to unmatch transaction');
    }
  }

  // Group transactions by month
  function groupByMonth(txns) {
    const groups = {};
    txns.forEach(t => {
      const date = t.cleared_date || t.scheduled_date;
      if (!date) return;
      const monthKey = format(parseISO(date + 'T00:00:00'), 'yyyy-MM');
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(t);
    });
    return groups;
  }

  // Filter transactions by search and category
  // Filter accounts dropdown based on entity selection
  const filteredAccountsList = useMemo(() => {
    if (activeFilter === 'all') return accountsList;
    if (activeFilter === 'personal') {
      const allLinkedIds = new Set();
      entities.forEach(e => (e.linkedAccountIds || []).forEach(id => allLinkedIds.add(id)));
      return accountsList.filter(acc => !allLinkedIds.has(acc.id));
    }
    const entity = entities.find(e => e.id === activeFilter);
    if (!entity) return accountsList;
    const linkedIds = new Set(entity.linkedAccountIds || []);
    return accountsList.filter(acc => linkedIds.has(acc.id));
  }, [accountsList, activeFilter, entities]);

  // Build set of visible account IDs for entity filtering
  const entityAccountIds = useMemo(() => {
    if (activeFilter === 'all') return null; // null means show all
    return new Set(filteredAccountsList.map(acc => acc.id));
  }, [activeFilter, filteredAccountsList]);

  // Reset account selection when entity filter changes
  useEffect(() => {
    if (selectedAccountId && filteredAccountsList.length > 0) {
      const stillVisible = filteredAccountsList.some(acc => String(acc.id) === String(selectedAccountId));
      if (!stillVisible) setSelectedAccountId('');
    }
  }, [filteredAccountsList, selectedAccountId]);

  const filteredTransactions = useCallback(() => {
    let filtered = transactions;

    // Entity filter — restrict to accounts belonging to selected entity
    if (entityAccountIds) {
      filtered = filtered.filter(t => entityAccountIds.has(t.bank_account_id));
    }
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => 
        (t.name || '').toLowerCase().includes(query) ||
        (t.merchant_name || '').toLowerCase().includes(query) ||
        (t.category || '').toLowerCase().includes(query) ||
        (t.notes || '').toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    return filtered;
  }, [transactions, searchQuery, selectedCategory, entityAccountIds]);

  const displayTransactions = filteredTransactions();
  const transactionsByMonth = groupByMonth(displayTransactions);
  const sortedMonths = Object.keys(transactionsByMonth).sort((a, b) => b.localeCompare(a));

  function toggleMonth(monthKey) {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  }

  function getMonthLabel(monthKey) {
    const date = parseISO(monthKey + '-01T00:00:00');
    return format(date, 'MMMM yyyy');
  }

  function getMonthSummary(txns) {
    let income = 0, expenses = 0;
    txns.forEach(t => {
      const amount = parseFloat(t.amount) || 0;
      if (t.item_type === 'income' || t.signedAmount > 0) {
        income += Math.abs(amount);
      } else {
        expenses += Math.abs(amount);
      }
    });
    return { income, expenses, net: income - expenses };
  }

  function handleRowContextMenu(e, txn) {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, txn });
  }

  function handleCreateRecurring(txn) {
    setCreateRecurringTxn(txn);
  }

  function handleLinkToForecast(txn) {
    setLinkToForecastTxn(txn);
  }

  function handleRecurringCreated() {
    setCreateRecurringTxn(null);
    // Optionally refresh - the new recurring item won't show in history,
    // but it confirms the action succeeded
  }

  // Render a single transaction row
  function renderTransactionRow(txn) {
    const isIncome = txn.item_type === 'income' || txn.signedAmount > 0;
    const amount = Math.abs(parseFloat(txn.amount) || 0);
    const typeConfig = TYPE_CONFIG[txn.item_type] || TYPE_CONFIG.bill;
    const statusConfig = STATUS_CONFIG[txn.status] || STATUS_CONFIG.cleared;
    const StatusIcon = statusConfig.icon;

    const isPlaid = txn.source === 'plaid';
    const isMatched = txn.match_status === 'matched' || txn.matched_transaction_id;
    const isPendingAtBank = txn.pending_at_bank === true;
    const forecast = txn.matched_forecast;

    return (
      <div key={txn.id}>
        {/* === MOBILE CARD (below sm) === */}
        <div
          onContextMenu={(e) => handleRowContextMenu(e, txn)}
          className={`sm:hidden p-3 bg-white border-b border-gray-100 active:bg-gray-50 cursor-context-menu ${isPendingAtBank ? 'opacity-70' : ''}`}
        >
          {/* Top: name + amount */}
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {isPlaid ? (
                  <Building2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                ) : (
                  <Banknote className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                )}
                <span className="text-base font-medium text-gray-900 truncate">
                  {txn.merchant_name || txn.name}
                </span>
                {isMatched && (
                  <Link2 className="w-3 h-3 text-green-500 flex-shrink-0" />
                )}
              </div>
            </div>
            <span className={`text-base font-semibold flex-shrink-0 ${isIncome ? 'text-emerald-600' : 'text-red-600'}`}>
              {isIncome ? '+' : '-'}{formatCurrency(amount)}
            </span>
          </div>

          {/* Middle: date + status + category */}
          <div className="flex items-center justify-between mt-1.5 text-sm">
            <div className="flex items-center gap-2">
              <span className="text-gray-500">
                {formatShortDate(txn.cleared_date || txn.scheduled_date)}
              </span>
              {txn.category && (
                <span className="bg-gray-100 px-1.5 py-0.5 rounded text-xs text-gray-500">{txn.category}</span>
              )}
            </div>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.bg} ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
          </div>

          {/* Account + balance */}
          {(txn.account_nickname || (txn.runningBalance !== null && txn.runningBalance !== undefined)) && (
            <div className="flex items-center justify-between mt-1 text-xs text-gray-400">
              {txn.account_nickname ? <span>{txn.account_nickname}</span> : <span />}
              {txn.runningBalance !== null && txn.runningBalance !== undefined && (
                <span className="text-gray-600">Bal: {formatCurrency(txn.runningBalance)}</span>
              )}
            </div>
          )}

          {/* Matched forecast details */}
          {isMatched && forecast && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-xs">
              <span className="text-emerald-600 font-medium">
                ↔ {forecast.recurring_name || forecast.name}
              </span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{formatCurrency(Math.abs(forecast.amount))}</span>
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">{formatShortDate(forecast.scheduled_date)}</span>
              {forecast.recurring_frequency && (
                <>
                  <span className="text-gray-400">·</span>
                  <span className="text-purple-500 capitalize">{forecast.recurring_frequency}</span>
                </>
              )}
              {parseInt(forecast.receipt_count) > 0 && (
                <button
                  onClick={(e) => { e.stopPropagation(); setReceiptViewerTxId(forecast.id); }}
                  className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200 min-h-[44px] sm:min-h-0"
                  title="View receipt"
                >
                  <Camera className="w-3 h-3" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleUnmatch(txn); }}
                className="ml-1 p-1 text-gray-400 hover:text-red-500 rounded transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center -my-2"
                title="Unmatch"
              >
                <Unlink className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* === DESKTOP ROW (sm+) === */}
        <div
          onContextMenu={(e) => handleRowContextMenu(e, txn)}
          className={`hidden sm:flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-context-menu ${isPendingAtBank ? 'opacity-70' : ''}`}
        >
          {/* Date */}
          <div className="w-24 flex-shrink-0">
            <span className="text-sm text-gray-600">
              {formatShortDate(txn.cleared_date || txn.scheduled_date)}
            </span>
          </div>

          {/* Source indicator */}
          <div className="w-8 flex-shrink-0">
            {isPlaid ? (
              <Building2 className="w-4 h-4 text-blue-500" title="Bank transaction" />
            ) : (
              <Banknote className="w-4 h-4 text-gray-400" title="Manual entry" />
            )}
          </div>

          {/* Description */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 truncate">
                {txn.merchant_name || txn.name}
              </span>
              {isMatched && (
                <Link2 className="w-3 h-3 text-green-500" title="Matched to forecast" />
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {txn.category && (
                <span className="bg-gray-100 px-1.5 py-0.5 rounded">{txn.category}</span>
              )}
              {txn.account_nickname && (
                <span>{txn.account_nickname}</span>
              )}
            </div>
            {/* Matched forecast details */}
            {isMatched && forecast && (
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className="text-emerald-600 font-medium">
                  ↔ {forecast.recurring_name || forecast.name}
                </span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{formatCurrency(Math.abs(forecast.amount))}</span>
                <span className="text-gray-400">·</span>
                <span className="text-gray-500">{formatShortDate(forecast.scheduled_date)}</span>
                {forecast.recurring_frequency && (
                  <>
                    <span className="text-gray-400">·</span>
                    <span className="text-purple-500 capitalize">{forecast.recurring_frequency}</span>
                  </>
                )}
                {parseInt(forecast.receipt_count) > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setReceiptViewerTxId(forecast.id); }}
                    className="inline-flex items-center gap-0.5 text-xs px-1 py-0.5 bg-amber-100 text-amber-700 rounded hover:bg-amber-200"
                    title="View receipt"
                  >
                    <Camera className="w-3 h-3" />
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); handleUnmatch(txn); }}
                  className="ml-1 p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                  title="Unmatch — return forecast item to upcoming"
                >
                  <Unlink className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>

          {/* Status */}
          <div className="w-20 flex-shrink-0">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.bg} ${statusConfig.color}`}>
              <StatusIcon className="w-3 h-3" />
              {statusConfig.label}
            </span>
          </div>

          {/* Amount */}
          <div className="w-28 text-right flex-shrink-0">
            <span className={`text-sm font-medium ${isIncome ? 'text-emerald-600' : 'text-red-600'}`}>
              {isIncome ? '+' : '-'}{formatCurrency(amount)}
            </span>
          </div>

          {/* Running Balance */}
          <div className="w-28 text-right flex-shrink-0">
            {txn.runningBalance !== null && txn.runningBalance !== undefined ? (
              <span className="text-sm text-gray-700">
                {formatCurrency(txn.runningBalance)}
              </span>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppShell>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold font-display text-gray-900">Transaction History</h1>
              <p className="text-base sm:text-sm text-gray-500">
                Checkbook register of past transactions
              </p>
              {plaidDataAsOf && (() => {
                const diffMs = new Date() - new Date(plaidDataAsOf);
                const diffMin = Math.floor(diffMs / 60000);
                const label = diffMin < 1 ? 'just now' : diffMin < 60 ? `${diffMin}m ago` : diffMin < 1440 ? `${Math.floor(diffMin/60)}h ago` : `${Math.floor(diffMin/1440)}d ago`;
                const isStale = diffMs > 4 * 60 * 60 * 1000;
                return (
                  <div className={`flex items-center gap-1 mt-1 text-xs ${isStale ? 'text-amber-600' : 'text-gray-400'}`}>
                    <Clock className="w-3 h-3" />
                    <span>Bank data as of {label}</span>
                    {isStale && <span className="hidden sm:inline font-medium">(may be stale)</span>}
                  </div>
                );
              })()}
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 self-start sm:self-auto min-h-[44px] sm:min-h-0"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
      </div>
      
      {/* Filters */}
      <div className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-0 sm:min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>

            {/* Date Preset */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={datePreset}
                onChange={(e) => setDatePreset(e.target.value)}
                className="flex-1 sm:flex-initial border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                {DATE_PRESETS.map(preset => (
                  <option key={preset.id} value={preset.id}>{preset.label}</option>
                ))}
              </select>
            </div>

            {/* Custom Date Range */}
            {datePreset === 'custom' && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-gray-500 text-center">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            )}

            {/* Account Filter */}
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                className="flex-1 sm:flex-initial border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Accounts</option>
                {filteredAccountsList.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.institutionName ? `${account.institutionName} — ` : ''}{account.nickname || account.name} {account.mask ? `(${account.mask})` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Category Filter */}
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 sm:flex-initial border border-gray-300 rounded-lg px-3 py-2 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              >
                <option value="">All Categories</option>
                {categoriesList.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Transaction List */}
      <div className="px-3 sm:px-6 py-3 sm:py-4">
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
            <p className="text-gray-500">Loading transactions...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl shadow-sm border border-red-200 p-8 text-center">
            <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : displayTransactions.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-gray-500">No transactions found</p>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {sortedMonths.map(monthKey => {
              const monthTxns = transactionsByMonth[monthKey];
              const monthSummary = getMonthSummary(monthTxns);
              const isExpanded = expandedMonths[monthKey];

              return (
                <div key={monthKey} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Month Header */}
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 sm:px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors gap-1 sm:gap-0 min-h-[44px] sm:min-h-0"
                  >
                    <div className="flex items-center gap-2 sm:gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="font-semibold text-gray-900 text-base">
                        {getMonthLabel(monthKey)}
                      </span>
                      <span className="text-xs sm:text-sm text-gray-500">
                        {monthTxns.length} txns
                      </span>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-6 text-xs sm:text-sm ml-7 sm:ml-0">
                      <span className="text-emerald-600">+{formatCurrency(monthSummary.income)}</span>
                      <span className="text-red-600">-{formatCurrency(monthSummary.expenses)}</span>
                      <span className={`font-medium ${monthSummary.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        <span className="hidden sm:inline">Net: </span>{monthSummary.net >= 0 ? '+' : ''}{formatCurrency(monthSummary.net)}
                      </span>
                    </div>
                  </button>

                  {/* Month Transactions */}
                  {isExpanded && (
                    <div>
                      {/* Column Headers - desktop only */}
                      <div className="hidden sm:flex items-center gap-3 px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="w-24 flex-shrink-0">Date</div>
                        <div className="w-8 flex-shrink-0">Src</div>
                        <div className="flex-1">Description</div>
                        <div className="w-20 flex-shrink-0">Status</div>
                        <div className="w-28 text-right flex-shrink-0">Amount</div>
                        <div className="w-28 text-right flex-shrink-0">Balance</div>
                      </div>

                      {/* Transaction Rows */}
                      {monthTxns.map(txn => renderTransactionRow(txn))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={[
            {
              label: 'Link to Existing Forecast',
              icon: Link2,
              onClick: () => handleLinkToForecast(contextMenu.txn)
            },
            {
              label: 'Create Recurring Forecast',
              icon: Plus,
              onClick: () => handleCreateRecurring(contextMenu.txn)
            }
          ]}
        />
      )}

      {/* Create Recurring Modal */}
      {createRecurringTxn && (
        <CreateRecurringModal
          txn={createRecurringTxn}
          accountsList={accountsList}
          onClose={() => setCreateRecurringTxn(null)}
          onSuccess={handleRecurringCreated}
        />
      )}

      {/* Link to Forecast Modal */}
      {linkToForecastTxn && (
        <LinkToForecastModal
          txn={linkToForecastTxn}
          onClose={() => setLinkToForecastTxn(null)}
          onSuccess={() => {
            setLinkToForecastTxn(null);
            loadTransactions();
          }}
        />
      )}
      {receiptViewerTxId && (
        <ReceiptViewer
          scheduledTransactionId={receiptViewerTxId}
          onClose={() => setReceiptViewerTxId(null)}
        />
      )}
    </AppShell>
  );
}
